import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  Middleware,
  offset,
  Placement,
  shift,
  useFloating,
} from "@floating-ui/react";
import {
  Popover as HeadlessUIPopover,
  PopoverBackdrop as HeadlessUIPopoverBackdrop,
  PopoverButton as HeadlessUIPopoverButton,
  PopoverPanel as HeadlessUIPopoverPanel,
  Portal,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { assignRefs, cx, useResizeObserver } from "@jugl-web/utils";
import {
  ComponentLifecycleListener,
  ComponentLifecycleListenerProps,
} from "@jugl-web/utils/utils/ComponentLifecycleListener";
import debounce from "lodash/debounce";
import {
  CSSProperties,
  forwardRef,
  Fragment,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
  useEffect,
  useRef,
  useState,
} from "react";

export interface RenderTriggerProps {
  Trigger: typeof HeadlessUIPopoverButton;
  triggerRef: Ref<HTMLButtonElement>;
  isOpen: boolean;
  onClose: () => void;
}

interface ChildrenProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface PopoverProps extends ComponentLifecycleListenerProps {
  children: ReactNode | ((props: ChildrenProps) => ReactNode);
  placement?: Placement;
  hasBackdrop?: boolean;
  backdropStyle?: "blur" | "transparent";
  adjustToTriggerWidth?: boolean;
  /**
   * Automatically update popover position on window resize/scroll
   * @warning There's are a lot of issues with z-indices (for top bar, dialogs, etc.)
   * so it has to be used with caution
   */
  autoPosition?: boolean;
  hasArrow?: boolean;
  arrowClassName?: string;
  floatingMiddleware?: Middleware[];
  isDisabled?: boolean;
  className?: string;
  style?: CSSProperties;
  isStatic?: boolean;
  renderTrigger: (props: RenderTriggerProps) => ReactElement;
  onOverlayClickCapture?: (event: MouseEvent) => void;
}

/**
 * z-index ladder (keep in sync with Drawer, Dialog, Alert, Tooltip and Menu):
 *   40    SidebarOverlay
 *   50    MainNavBar (while the sidebar overlay is open)
 *   110   Drawer panel + backdrop, Dialog backdrop
 *   1999  Popover backdrop
 *   2000  Dialog / Alert panels, toasts
 *   2001  Tooltip (`--z-index-tooltip`)
 *   10000 Popover / Menu panels
 *
 * A Popover opened from inside a Headless UI Dialog (Drawer, Dialog, Alert) is
 * portaled into that dialog's root element after its panel, so it paints above
 * its host regardless of the values above.
 */
export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      children,
      placement,
      hasBackdrop,
      backdropStyle,
      adjustToTriggerWidth,
      autoPosition,
      hasArrow,
      arrowClassName,
      floatingMiddleware,
      isDisabled,
      className,
      style,
      isStatic,
      renderTrigger,
      onOverlayClickCapture,
      onMount,
      onUnmount,
    },
    forwardedRef
  ) => {
    const arrowRef = useRef<SVGSVGElement | null>(null);
    const [popoverWidth, setPopoverWidth] = useState<number>();

    const { refs, floatingStyles, elements, update, context } =
      useFloating<HTMLButtonElement>({
        placement,
        middleware: !floatingMiddleware
          ? [
              flip({
                fallbackStrategy: "bestFit",
                fallbackAxisSideDirection: "start",
              }),
              shift(),
              offset({ mainAxis: 4 }),
              arrow({ element: arrowRef, padding: 8 }),
            ]
          : floatingMiddleware,
        whileElementsMounted: autoPosition ? autoUpdate : undefined,
      });

    const { ref: panelRef } = useResizeObserver({ onResize: update });

    useEffect(() => {
      const element =
        elements.reference instanceof Element ? elements.reference : null;

      if (!adjustToTriggerWidth || !element) {
        setPopoverWidth(undefined);
        return undefined;
      }

      const debouncedUpdate = debounce(() => {
        setPopoverWidth(element.getBoundingClientRect().width);
      }, 300);

      const observer = new ResizeObserver(debouncedUpdate);

      observer.observe(element);

      return () => {
        observer.disconnect();
        debouncedUpdate.cancel();
      };
    }, [adjustToTriggerWidth, elements.reference]);

    return (
      <HeadlessUIPopover as={Fragment}>
        {({ open, close }) => (
          <>
            {renderTrigger({
              Trigger: HeadlessUIPopover.Button,
              triggerRef: refs.setReference,
              isOpen: open,
              onClose: close,
            })}
            {!isDisabled && (
              <Portal>
                <Transition as={Fragment}>
                  <TransitionChild
                    as={Fragment}
                    enter="transition-opacity"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <HeadlessUIPopoverPanel
                      as="div"
                      ref={assignRefs([
                        refs.setFloating,
                        forwardedRef,
                        panelRef,
                      ])}
                      {...(isStatic
                        ? { static: true }
                        : // `unmount` has to be `true` to make the ComponentLifecycleListener work
                          { unmount: true })}
                      className={cx(
                        "absolute z-10000 rounded-lg bg-white",
                        className
                      )}
                      style={{
                        boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.12)",
                        width: popoverWidth,
                        ...floatingStyles,
                        ...style,
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {typeof children === "function"
                        ? children({ isOpen: open, onClose: close })
                        : children}
                      {hasArrow && (
                        <FloatingArrow
                          ref={arrowRef}
                          // @ts-expect-error Some type mismatch in the `floating-ui` package
                          context={context}
                          className={arrowClassName}
                        />
                      )}
                      <ComponentLifecycleListener
                        onMount={onMount}
                        onUnmount={onUnmount}
                      />
                    </HeadlessUIPopoverPanel>
                  </TransitionChild>
                  {hasBackdrop && (
                    <TransitionChild
                      as={Fragment}
                      enter="transition-opacity"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition-opacity"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <HeadlessUIPopoverBackdrop
                        className={cx(
                          "fixed inset-0 z-[1999]",
                          backdropStyle === "blur" &&
                            "bg-[rgba(33,58,72,0.38)] backdrop-blur-[1px]"
                        )}
                        onClickCapture={onOverlayClickCapture}
                      />
                    </TransitionChild>
                  )}
                </Transition>
              </Portal>
            )}
          </>
        )}
      </HeadlessUIPopover>
    );
  }
);
